import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Button } from "@/shared/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/Dialog";
import { FormField } from "@/shared/components/form/FormField";
import { TextareaFormField } from "@/shared/components/form/TextareaFormField";
import { useCreateList } from "@/shared/lib/lists";

interface NewListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal form for creating a new list. On success, sets the contacts page
 * scope to the new list via `?list=<id>` and closes.
 */
export function NewListDialog({ open, onOpenChange }: NewListDialogProps) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const create = useCreateList();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      create.reset();
    }
  }, [open]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate(
      { name: trimmed, description: description.trim() || null },
      {
        onSuccess: (list) => {
          onOpenChange(false);
          // If we're already on /app/contacts, swap the param; otherwise
          // navigate over.
          if (window.location.pathname === "/app/contacts") {
            setSearchParams({ list: list.id });
          } else {
            navigate(`/app/contacts?list=${list.id}`);
          }
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New list</DialogTitle>
          <DialogDescription>
            Group contacts into a saved scope you can switch into at any time.
          </DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <FormField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Holiday Cards"
            required
            autoFocus
          />
          <TextareaFormField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            rows={3}
          />
          {create.error ? (
            <p className="mx-4 type-body-sm text-danger-strong">
              {create.error.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              {create.isPending ? "Creating…" : "Create list"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
